import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import SelectInput from "../components/select-input";
import DataTable, { TableRowData } from "../components/data-table";
import { usersTableColumns } from "../lib/table-columns";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import FormDrawer from "../components/form-drawer";
import { usersFormFields } from "../lib/form-fields";
import SearchInput from "../components/search-input";
import { Edit } from "lucide-react";
import {
  getUsers,
  getUserStatistics,
  createUser,
  updateUser,
  CreateUserPayload,
  UpdateUserPayload,
  UserStatistics,
} from "../lib/api";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";

const Users: React.FC = () => {
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formValues, setFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);

  const queryClient = useQueryClient();
  const roleParam = currentTab !== "All" ? currentTab : undefined;

  const usersQuery = useQuery({
    queryKey: ["users", { page: currentPage, limit: pageSize, search: searchTerm, role: roleParam }],
    queryFn: async () => getUsers(currentPage, pageSize, searchTerm, roleParam),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<UserStatistics>({
    queryKey: ["users", "statistics"],
    queryFn: async () => getUserStatistics(),
  });

  useEffect(() => {
    if (!usersQuery.error) return;
    toast.error(getBackendErrorMessage(usersQuery.error, "Failed to fetch users"));
  }, [usersQuery.error]);

  useEffect(() => {
    if (!statisticsQuery.error) return;
    toast.error(getBackendErrorMessage(statisticsQuery.error, "Failed to fetch user statistics"));
  }, [statisticsQuery.error]);

  const users = useMemo(() => {
    const data = usersQuery.data?.data ?? [];
    const mapped = data.map((u) => ({
      id: u.id,
      name: u.fullName,
      email: u.email,
      phone: u.phoneNumber,
      location: [u.ward, u.district, u.region].filter(Boolean).join(", ") || "—",
      role: u.role,
      status: u.status,
      raw_data: u,
    })) as TableRowData[];

    if (!selectedStatus || selectedStatus === "all") return mapped;
    return mapped.filter((u) =>
      String((u as any).raw_data?.status ?? u.status)
        .toLowerCase()
        .includes(selectedStatus.toLowerCase()),
    );
  }, [usersQuery.data, selectedStatus]);

  const statistics = statisticsQuery.data ?? null;
  const totalItems = usersQuery.data?.meta.total ?? 0;
  const totalPages = usersQuery.data?.meta.totalPages ?? 1;
  const initialLoading = usersQuery.isLoading || statisticsQuery.isLoading;
  const tableLoading = usersQuery.isFetching && !usersQuery.isLoading;

  // Reset pagination on tab or status changes (search handled separately).
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab, selectedStatus]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), reset pagination.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);
        const location = [raw.ward, raw.district, raw.region].filter(Boolean).join(", ");
        setFormValues({
          name: raw.fullName,
          phone: raw.phoneNumber,
          email: raw.email,
          street: raw.street ?? "",
          location: location || "",
          role: raw.role,
          status: raw.status,
        });
      },
    },
    {
      label: "Edit user",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);
        const location = [raw.ward, raw.district, raw.region].filter(Boolean).join(", ");
        setFormValues({
          name: raw.fullName,
          phone: raw.phoneNumber,
          email: raw.email,
          street: raw.street ?? "",
          location: location || "",
          role: raw.role,
          status: raw.status,
        });
      },
    },
  ];

  const handleFormSubmit = async () => {
    if (!formValues) return;

    setIsSubmitting(true);

    const locationStr = (formValues.location as string) || "";
    const locationParts = locationStr.split(",").map((s: string) => s.trim());
    const region = locationParts.length > 0 ? locationParts[locationParts.length - 1] : "";
    const district = locationParts.length > 1 ? locationParts[locationParts.length - 2] : "";
    const ward = locationParts.length > 2 ? locationParts[locationParts.length - 3] : "";

    try {
      if (editingId) {
        const payload: UpdateUserPayload = {
          fullName: formValues.name as string,
          phoneNumber: formValues.phone as string,
          email: formValues.email as string,
          role: formValues.role as any,
          status: formValues.status as any,
          street: (formValues.street as string) || undefined,
          ward: ward || undefined,
          district: district || undefined,
          region: region || undefined,
          country: region || ward || district ? "Tanzania" : undefined,
        };
        await updateUser(editingId, payload);
      } else {
        const payload: CreateUserPayload = {
          fullName: formValues.name as string,
          phoneNumber: formValues.phone as string,
          email: formValues.email as string,
          role: formValues.role as any,
          street: (formValues.street as string) || undefined,
          ward: ward || undefined,
          district: district || undefined,
          region: region || undefined,
          country: region || ward || district ? "Tanzania" : undefined,
        };
        await createUser(payload);
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["users", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save user"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleMetrics =
    (statistics?.byRole.map((r) => ({
      label: r.role,
      count: r.count,
      percentage: Math.round((r.count / (statistics?.totalUsers || 1)) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  const statusData =
    statistics?.byStatus.breakdown.map((s) => ({
      category: s.status,
      gap: 0.2,
      values: [
        {
          label: "Count",
          value: s.count,
          fillColor: s.status === "ACTIVE" ? "#bce3ff" : "#ffb3b3",
        },
      ],
    })) || [];

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["users"] }),
      queryClient.invalidateQueries({ queryKey: ["users", "statistics"] }),
    ]);

  const openNewUser = () => setFormValues({});

  const statusSelect = (
    <SelectInput
      value={selectedStatus}
      options={[
        { label: "All", value: "all" },
        { label: "Active", value: "active" },
        { label: "Disabled", value: "disabled" },
      ]}
      placeholder={"Status"}
      onChange={(value) => {
        setSelectedStatus(value);
      }}
    />
  );

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit User" : "Add New User"}
        buttonText={editingId ? "Update User" : "Add User"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={usersFormFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <PageShell
        title="Users"
        description="Manage system users how view and interact with system."
        loading={initialLoading}
        toolbar={
          <>
            <RefreshToolbarButton
              isRefreshing={usersQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewUser}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New User
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Users",
            value: statistics?.totalUsers.toString() || "0",
            icon: <Icons.Candidate className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Active Users",
            value: statistics?.byStatus.active.toString() || "0",
            icon: <Icons.Candidate className="w-5 h-5 text-green-600" />,
          },
          {
            label: "Disabled Users",
            value: statistics?.byStatus.disabled.toString() || "0",
            icon: <Icons.Candidate className="w-5 h-5 text-red-600" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4">
            <HorizontalBarGraph graphTitle="Users by Status" data={statusData} />
            <BarGraph graphTitle="Users by Role" metrics={roleMetrics.slice(0, 3)} />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New User", icon: Icons.Plus, onClick: openNewUser },
          filters: <FilterField label="Status">{statusSelect}</FilterField>,
          activeFilterCount: selectedStatus !== "all" ? 1 : 0,
          onResetFilters: () => setSelectedStatus("all"),
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabBar>
            {[
              { label: "All", count: statistics?.totalUsers || 0 },
              ...(statistics?.byRole.map((r) => ({ label: r.role, count: r.count })) || []),
            ].map((tab) => (
              <TabButton
                key={tab.label}
                label={tab.label}
                count={tab.count}
                active={currentTab === tab.label}
                onClick={() => setCurrentTab(tab.label)}
              />
            ))}
          </TabBar>
          <DesktopOnly>{statusSelect}</DesktopOnly>
        </div>
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search users..."
        />
        <DataTable
          columns={usersTableColumns}
          data={users}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </PageShell>
    </>
  );
};

export default Users;
